/**
 * Schema entries for the "Interview details" section of the Details step.
 *
 * Covers: interview name → interview type → recommended-interviewer pill →
 * interviewer select → status banner → multi-interviewer info banner →
 * meeting room checkbox + select.
 *
 * Pure builder function — receives all dependencies as args, returns
 * schema entries. No React hooks called here.
 */
import { Flex, Text } from '@chakra-ui/react';
import {
  CannotRecommendBanner,
  InterviewerStatusBanner,
  RecommendedInterviewerPill,
  type InterviewerStatusMap,
} from '../../fields/interviewer';
import { CenterSpinner } from '@/components/ats';
import { copy, INTERVIEW_TYPE_OPTIONS } from './constants';
import type { SuggestedInterviewer } from '@/api/suggestedInterviewer';

interface InterviewDetailsSchemaDeps {
  interviewPlan?: Record<string, unknown>;
  interviewerField: Record<string, unknown>;
  meetingRoomField: Record<string, unknown>;
  meetingRoomsEnabled: boolean;
  isMultiInterviewer: boolean;
  isSelfSchedule: boolean;
  canRecommend: boolean;
  isRecommending: boolean;
  recommendedUser: SuggestedInterviewer | null | undefined;
  onInterviewerRecommend?: (_id: string) => void;
}

export function buildInterviewDetailsSchema({
  interviewPlan,
  interviewerField,
  meetingRoomField,
  meetingRoomsEnabled,
  isMultiInterviewer,
  isSelfSchedule,
  canRecommend,
  isRecommending,
  recommendedUser,
  onInterviewerRecommend,
}: InterviewDetailsSchemaDeps): unknown[] {
  const meetingRoomFields = meetingRoomsEnabled
    ? [
        {
          type: 'checkbox' as const,
          name: 'book_meeting_room',
          fieldProps: { label: copy.meetingRoom.checkboxLabel },
        },
        meetingRoomField,
      ]
    : [];

  return [
    {
      type: 'sectionLabel',
      label: copy.sectionLabel,
    },
    {
      type: 'input',
      name: 'interviewName',
      label: copy.interviewName.label,
      required: true,
      fieldProps: {
        placeholder: copy.interviewName.placeholder,
        maxLength: 250,
      },
    },
    {
      type: 'select',
      name: 'interview_type',
      label: copy.interviewType.label,
      required: true,
      options: INTERVIEW_TYPE_OPTIONS,
      fieldProps: { searchable: false },
    },

    // AI-recommended interviewer pill (manual one-on-one create only).
    // Renders ABOVE the interviewer select. Shows a spinner while the
    // API is polling, then the pill once a result arrives.
    // Disappears once the user has picked an interviewer.
    //
    // When the recommendation can't be produced (empty interview plan
    // or API returned no candidate), `CannotRecommendBanner` renders
    // in the same slot — legacy `SchInterviewerFieldNoRecommend`
    // parity (see KInterviewScheduler).
    ...(canRecommend
      ? [
          {
            type: 'custom' as const,
            name: 'recommendedInterviewerPill',
            render: ({
              formMethods,
            }: {
              formMethods: {
                setValue: (_n: string, _v: unknown) => void;
                watch: (_n: string) => unknown;
              };
            }) => {
              const current = formMethods.watch('participant_ids');
              const hasValue = Array.isArray(current)
                ? current.length > 0
                : !!current;
              if (hasValue) return null;
              if (isRecommending) {
                return (
                  <Flex
                    align="center"
                    gap="4"
                    bg="primary-25"
                    borderRadius="md"
                    px="3"
                    py="2"
                    mb="-2"
                  >
                    <CenterSpinner h="fit-content" />
                    <Text fontSize="sm" color="primary-600">
                      Recommonding best interviewer…
                    </Text>
                  </Flex>
                );
              }
              if (recommendedUser) {
                return (
                  <RecommendedInterviewerPill
                    user={recommendedUser}
                    onSelect={(u) => {
                      const id = String(u.id);
                      formMethods.setValue('participant_ids', id);
                      onInterviewerRecommend?.(id);
                    }}
                  />
                );
              }
              // No spinner, no recommendation → tell the user why.
              // Distinguish "plan is empty" from "API returned null":
              //   - empty interviewers in the plan → empty-plan
              //   - non-empty plan but no recommendation came back →
              //     no-recommendation
              const planInterviewers = Array.isArray(
                interviewPlan?.interviewers
              )
                ? (interviewPlan?.interviewers as unknown[])
                : [];
              const reason =
                planInterviewers.length === 0
                  ? 'empty-plan'
                  : 'no-recommendation';
              return <CannotRecommendBanner reason={reason} />;
            },
          },
        ]
      : []),

    {
      ...interviewerField,
      // Disable while the recommendation is loading so the user
      // waits for the suggestion before manually picking.
      disabled: canRecommend && isRecommending,
      // String required message overrides FormBuilder's generic default
      // for both single and multi select shapes.
      required: isMultiInterviewer
        ? 'At least one interviewer is required'
        : 'Interviewer is required',
    },

    // Workload / availability banner — always mounted; renders null
    // when no flags are set on the plan.
    {
      type: 'custom' as const,
      name: 'interviewerStatusBanner',
      render: ({
        formMethods,
      }: {
        formMethods: { watch: (_n: string) => unknown };
      }) => {
        const raw = formMethods.watch('participant_ids');
        const ids: Array<string | number> = Array.isArray(raw)
          ? (raw as Array<string | number>)
          : raw
            ? [raw as string | number]
            : [];
        const status = interviewPlan?.interviewer_status as
          | InterviewerStatusMap
          | undefined;
        return <InterviewerStatusBanner selectedIds={ids} status={status} />;
      },
    },

    // Self-schedule only: warn that >1 interviewer in a one-on-one means
    // the candidate's slot pick decides who attends.
    ...(isSelfSchedule
      ? [
          {
            type: 'custom' as const,
            name: 'multiInterviewerInfo',
            render: ({
              formMethods,
            }: {
              formMethods: { watch: (_n: string) => unknown };
            }) => {
              const type = formMethods.watch('interview_type');
              const ids = formMethods.watch('participant_ids');
              const count = Array.isArray(ids) ? ids.length : 0;
              if (type !== 'one_on_one' || count <= 1) return null;
              return (
                <Flex
                  flexDir="column"
                  gap="2"
                  mt="-3"
                  bg="secondary-50"
                  borderRadius="md"
                  p="3"
                >
                  <Text fontSize="sm" fontWeight="medium" color="secondary-900">
                    Multiple interviewers in one-on-one interviews
                  </Text>
                  <Text fontSize="sm" color="secondary-700">
                    Since you are sharing the availability of multiple
                    interviewers, one interviewer will be selected based on the
                    time slot picked by the candidate.
                  </Text>
                </Flex>
              );
            },
          },
        ]
      : []),

    ...meetingRoomFields,
  ];
}
